import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { SettingsProvider } from './context/SettingsContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { Toaster } from 'sonner';
import Home from './pages/Home';                                  // eager (default route)
import WhatsAppButton from './components/WhatsAppButton';
import CompareBar from './components/CompareBar';
import MobileStickyCTA from './components/MobileStickyCTA';
import ScrollToTopButton from './components/ScrollToTopButton';

// Native Browser-Scroll-Restoration abschalten — wir machen das selbst,
// sonst springt Chrome nach Seitenwechseln manchmal auf eine alte Position
// oder resettet auf 0, auch wenn wir restaurieren wollen.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  try { window.history.scrollRestoration = 'manual'; } catch { /* noop */ }
}
import CookieConsent from './components/CookieConsent';
import CartHoldCountdown from './components/CartHoldCountdown';
import AnnouncementPopup from './components/AnnouncementPopup';
import AIChatWidget from './components/AIChatWidget';
import PageviewTracker from './components/PageviewTracker';
import PromoBanner from './components/PromoBanner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import './App.css';

// Code-splitting (Phase 70): every non-home page is fetched on demand. Reduces
// the initial JS payload from one giant bundle (~1.5 MB) to small per-route
// chunks (~50–200 kB each). Result: faster first paint everywhere.
const Products             = lazy(() => import('./pages/Products'));
const ProductDetail        = lazy(() => import('./pages/ProductDetail'));
const Service              = lazy(() => import('./pages/Service'));
const Kinderbetreuung      = lazy(() => import('./pages/Kinderbetreuung'));
const CustomerAction       = lazy(() => import('./pages/CustomerAction'));
const About                = lazy(() => import('./pages/About'));
const References           = lazy(() => import('./pages/References'));
const Admin                = lazy(() => import('./pages/Admin'));
const AdminLogin           = lazy(() => import('./pages/AdminLogin'));
const Mietanfrage          = lazy(() => import('./pages/Mietanfrage'));
const UmfrageSeite         = lazy(() => import('./pages/UmfrageSeite'));
const CompareProducts      = lazy(() => import('./pages/CompareProducts'));
const DynamicPage          = lazy(() => import('./pages/DynamicPage'));
const SlugResolver         = lazy(() => import('./pages/SlugResolver'));
const StandortLanding      = lazy(() => import('./pages/StandortLanding'));
const PublicContractSign   = lazy(() => import('./pages/PublicContractSign'));
const PublicProtocolDownload = lazy(() => import('./pages/PublicProtocolDownload'));
const BlogList             = lazy(() => import('./pages/BlogPages').then((m) => ({ default: m.BlogList })));
const BlogDetail           = lazy(() => import('./pages/BlogPages').then((m) => ({ default: m.BlogDetail })));
const BuchungskalenderPage = lazy(() => import('./pages/BuchungskalenderPage'));
const CustomerLogin        = lazy(() => import('./pages/CustomerLogin'));
const CustomerDashboard    = lazy(() => import('./pages/CustomerDashboard'));
const AuthCallback         = lazy(() => import('./pages/AuthCallback'));
const BookingConfirmation  = lazy(() => import('./pages/BookingConfirmation'));
const PasswordResetRequest = lazy(() => import('./pages/PasswordResetRequest'));
const PasswordResetConfirm = lazy(() => import('./pages/PasswordResetConfirm'));
const DriverApp            = lazy(() => import('./pages/DriverApp'));
const GuestReviewPage      = lazy(() => import('./pages/GuestReviewPage'));
const SecondHandPage       = lazy(() => import('./pages/SecondHandPage'));
const GuestbookPage        = lazy(() => import('./pages/GuestbookPage'));
const GuestbookUploadPage  = lazy(() => import('./pages/GuestbookUploadPage'));
const FAQPage              = lazy(() => import('./pages/FAQPage'));

// Minimal full-screen loader (no flash for fast chunks).
const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center" data-testid="route-loader">
    <div className="h-8 w-8 rounded-full border-2 border-sky-200 border-t-sky-600 animate-spin" />
  </div>
);

function AppRoutes() {
  const location = useLocation();
  const navType = useNavigationType();

  // Native Scroll-Restoration des Browsers abschalten — wir handeln sie selbst,
  // sonst resetten manche Browser nach dem Mounten auf 0 (überschreibt unser Restore).
  React.useEffect(() => {
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch { /* noop */ }
  }, []);

  // Scroll-Position laufend für die AKTUELLE Route speichern. KEIN Save im
  // Cleanup, weil zu dem Zeitpunkt die neue Seite schon den Viewport auf 0
  // zurückgesetzt haben kann → wir würden den falschen Wert sichern. Der
  // scroll-Event feuert sowieso bei jeder Nutzer-Scroll-Aktion, also haben
  // wir immer einen aktuellen Snapshot vor dem Verlassen.
  React.useEffect(() => {
    const path = location.pathname;
    let saveTimer = null;
    const schedule = () => {
      // Während aktiver Restore nichts überschreiben
      if (window.__kevRestoring) return;
      if (saveTimer) return;
      saveTimer = setTimeout(() => {
        saveTimer = null;
        if (window.__kevRestoring) return;
        try { sessionStorage.setItem('kev_scroll:' + path, String(window.scrollY || 0)); } catch { /* noop */ }
      }, 80);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      if (saveTimer) clearTimeout(saveTimer);
      // KEIN Save hier — würde den falschen (gerade resetteten) Wert sichern.
    };
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.hash) return; // Anker-Links eigenständig behandeln lassen
    if (navType === 'POP') {
      let targetY = 0;
      try {
        const saved = sessionStorage.getItem('kev_scroll:' + location.pathname);
        if (saved != null) targetY = parseInt(saved, 10) || 0;
      } catch { /* ignore */ }
      // Flag setzen, damit der Scroll-Listener nicht gleich wieder überschreibt
      window.__kevRestoring = true;
      let attempts = 0;
      const maxAttempts = 100;
      const stepMs = 100;
      const forceScroll = () => {
        if (Math.abs((window.scrollY || 0) - targetY) > 20) {
          window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
        }
      };
      const tryRestore = () => {
        const scrollHeight = document.documentElement.scrollHeight;
        if (scrollHeight >= targetY + 200 || attempts >= maxAttempts) {
          window.scrollTo({ top: targetY, left: 0, behavior: 'auto' });
          // Mehrere Nachkorrekturen, falls spät geladene Bilder/Banners die
          // Layout-Höhe nachträglich verschieben.
          setTimeout(forceScroll, 300);
          setTimeout(forceScroll, 700);
          setTimeout(forceScroll, 1300);
          setTimeout(() => { window.__kevRestoring = false; }, 1500);
        } else {
          attempts += 1;
          setTimeout(tryRestore, stepMs);
        }
      };
      tryRestore();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.hash, navType]);
  // Emergent Google OAuth callback detection (session_id in URL fragment) — must
  // run synchronously during render to avoid auth-check race conditions.
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/contract/sign/:token" element={<PublicContractSign />} />
        <Route path="/protocol/:token" element={<PublicProtocolDownload />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/produkte" element={<Products />} />
        <Route path="/produkte/:id" element={<ProductDetail />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/service" element={<Service />} />
        <Route path="/kinderbetreuung-service" element={<Kinderbetreuung />} />
        <Route path="/aktion/:token" element={<CustomerAction />} />
        <Route path="/ueber-uns" element={<About />} />
        <Route path="/about" element={<About />} />
        <Route path="/referenzen" element={<References />} />
        <Route path="/references" element={<References />} />
        <Route path="/mietanfrage" element={<Mietanfrage />} />
        <Route path="/verkauf" element={<SecondHandPage />} />
        <Route path="/second-hand" element={<SecondHandPage />} />
        <Route path="/kundenmomente" element={<GuestbookPage />} />
        <Route path="/kundenmomente/upload" element={<GuestbookUploadPage />} />
        {/* Phase 128.66 — Alte Links aus früheren Bewertungs-Mails weiterleiten */}
        <Route path="/gaestebuch" element={<Navigate to="/kundenmomente" replace />} />
        <Route path="/gaestebuch/upload" element={<Navigate to="/kundenmomente/upload" replace />} />
        <Route path="/umfrage" element={<UmfrageSeite />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/poll" element={<UmfrageSeite />} />
        <Route path="/vergleich" element={<CompareProducts />} />
        <Route path="/buchungskalender" element={<BuchungskalenderPage />} />
        <Route path="/seite/:slug" element={<DynamicPage />} />
        <Route path="/standorte/:slug" element={<StandortLanding />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<CustomerLogin />} />
        <Route path="/mein-konto" element={<CustomerDashboard />} />
        <Route path="/passwort-vergessen" element={<PasswordResetRequest />} />
        <Route path="/passwort-zuruecksetzen/:token" element={<PasswordResetConfirm />} />
        <Route path="/buchungsbestaetigung/:id" element={<BookingConfirmation />} />
        <Route path="/fahrer" element={<DriverApp />} />
        <Route path="/bewerten/:bookingId" element={<GuestReviewPage />} />
        {/* Catch-all: matches /<slug> for categories, landing-page variants and CMS pages. MUST be last. */}
        <Route path="/:slug" element={<SlugResolver />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ChunkErrorBoundary>
      <HelmetProvider>
        <SettingsProvider>
          <CustomerAuthProvider>
            <CartProvider>
              <FavoritesProvider>
                <BrowserRouter>
                  <PromoBanner />
                  <AppRoutes />
                  <PageviewTracker />
                  <WhatsAppButton />
                  <CompareBar />
                  <MobileStickyCTA />
                  <ScrollToTopButton />
                  <CookieConsent />
                  <CartHoldCountdown />
                  <AnnouncementPopup />
                  <AIChatWidget />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </FavoritesProvider>
            </CartProvider>
          </CustomerAuthProvider>
        </SettingsProvider>
      </HelmetProvider>
    </ChunkErrorBoundary>
  );
}

export default App;
